import {
  createFileRoute,
  Link,
  useNavigate,
  redirect,
} from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { registerSchema, type RegisterFormData } from '@/schemas/auth'
import { useAuthStore } from '@/stores/auth-store'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useState, useEffect, ComponentProps } from 'react'
import { Eye, EyeOff, Loader2, Brain, X } from 'lucide-react'
import { BackgroundWrapper } from '@/components/ui/background-wrapper'
import { getPasswordStrength } from '@/lib/utils'

function PasswordStrengthIndicator({ password }: { password: string }) {
  const { score, feedback } = getPasswordStrength(password)

  const getStrengthColor = (score: number) => {
    if (score <= 1) return 'bg-red-500'
    if (score <= 3) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  const getStrengthText = (score: number) => {
    if (score <= 1) return 'Weak'
    if (score <= 3) return 'Medium'
    return 'Strong'
  }

  if (!password) return null

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Password strength</span>
        <span
          className={`text-xs font-medium ${
            score <= 1
              ? 'text-red-500'
              : score <= 3
                ? 'text-yellow-500'
                : 'text-green-500'
          }`}
        >
          {getStrengthText(score)}
        </span>
      </div>
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map(level => (
          <div
            key={level}
            className={`h-1 w-full rounded-full ${
              level <= score ? getStrengthColor(score) : 'bg-muted'
            }`}
          />
        ))}
      </div>
      {feedback.length > 0 && (
        <div className="space-y-1">
          {feedback.map((item, index) => (
            <div key={index} className="flex items-center space-x-2">
              <X className="h-3 w-3 text-red-500" />
              <span className="text-xs text-muted-foreground">{item}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function RegisterForm({ className, ...props }: ComponentProps<'div'>) {
  const navigate = useNavigate()
  const {
    register: registerUser,
    googleSignIn,
    isLoading,
    error,
    validationErrors,
    resetAuthState,
  } = useAuthStore()
  const [showPassword, setShowPassword] = useState(false)

  // Reset auth state when component mounts (handles back navigation from OAuth)
  useEffect(() => {
    resetAuthState()
  }, [resetAuthState])

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  })

  const password = watch('password')

  const sanitizeUsername = (email: string): string => {
    // Extract everything before @ from email
    const emailPrefix = email.split('@')[0]

    // Guard against undefined prefix
    if (!emailPrefix) {
      return 'user'
    }

    // Remove any characters not in allowed set
    // Allowed: letters (a-z, A-Z), numbers (0-9), underscores (_), hyphens (-)
    let sanitized = emailPrefix.replace(/[^a-zA-Z0-9_-]/g, '')

    // Ensure username is not empty after sanitization
    if (!sanitized) {
      sanitized = 'user'
    }

    return sanitized
  }

  const onSubmit = async (data: RegisterFormData) => {
    try {
      // Extract and sanitize username from email
      const username = sanitizeUsername(data.email)

      // Add required fields for API
      const registrationData = {
        ...data,
        username,
        termsAccepted: true,
        privacyAccepted: true,
      }

      await registerUser(registrationData)
      navigate({ to: '/dashboard', replace: true })
    } catch {
      const { validationErrors: currentErrors } = useAuthStore.getState()
      if (currentErrors) {
        Object.entries(currentErrors).forEach(([field, messages]) => {
          setError(field as keyof RegisterFormData, {
            type: 'server',
            message: messages[0],
          })
        })
      }
    }
  }

  const handleGoogleSignUp = async () => {
    try {
      await googleSignIn()
    } catch {
      // Error is handled by the store
    }
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className="overflow-hidden p-0 bg-white dark:bg-white text-black dark:text-black border-gray-200 dark:border-gray-200 shadow-sm dark:shadow-sm">
        <CardContent className="p-6 md:p-8">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center text-center">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white border border-gray-200">
                    <Brain className="h-5 w-5 text-black" />
                  </div>
                  <span className="text-lg font-semibold">ThoughtFabric</span>
                </div>
                <h1 className="text-2xl font-bold">Create an account</h1>
                <p className="text-xs text-muted-foreground text-balance">
                  Enter your details below to create your account
                </p>
              </div>

              {error && (
                <div className="bg-destructive/15 border border-destructive/20 text-destructive text-xs px-4 py-3 rounded-md">
                  {error}
                </div>
              )}

              <div className="grid gap-3">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  {...register('email')}
                  className={cn(
                    'bg-white dark:bg-white border-gray-200 dark:border-gray-200 text-black dark:text-black placeholder:text-gray-500 dark:placeholder:text-gray-500',
                    errors.email || validationErrors?.email
                      ? 'border-destructive'
                      : ''
                  )}
                />
                {errors.email && (
                  <p className="text-xs text-destructive">
                    {errors.email.message}
                  </p>
                )}
                {validationErrors?.email && (
                  <p className="text-xs text-destructive">
                    {validationErrors.email[0]}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-3">
                  <Label htmlFor="firstName">First name</Label>
                  <Input
                    id="firstName"
                    placeholder="John"
                    {...register('firstName')}
                    className={cn(
                      'bg-white dark:bg-white border-gray-200 dark:border-gray-200 text-black dark:text-black placeholder:text-gray-500 dark:placeholder:text-gray-500',
                      errors.firstName || validationErrors?.firstName
                        ? 'border-destructive'
                        : ''
                    )}
                  />
                  {errors.firstName && (
                    <p className="text-xs text-destructive">
                      {errors.firstName.message}
                    </p>
                  )}
                  {validationErrors?.firstName && (
                    <p className="text-xs text-destructive">
                      {validationErrors.firstName[0]}
                    </p>
                  )}
                </div>
                <div className="grid gap-3">
                  <Label htmlFor="lastName">Last name</Label>
                  <Input
                    id="lastName"
                    placeholder="Doe"
                    {...register('lastName')}
                    className={cn(
                      'bg-white dark:bg-white border-gray-200 dark:border-gray-200 text-black dark:text-black placeholder:text-gray-500 dark:placeholder:text-gray-500',
                      errors.lastName || validationErrors?.lastName
                        ? 'border-destructive'
                        : ''
                    )}
                  />
                  {errors.lastName && (
                    <p className="text-xs text-destructive">
                      {errors.lastName.message}
                    </p>
                  )}
                  {validationErrors?.lastName && (
                    <p className="text-xs text-destructive">
                      {validationErrors.lastName[0]}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid gap-3">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    {...register('password')}
                    className={cn(
                      'bg-white dark:bg-white border-gray-200 dark:border-gray-200 text-black dark:text-black placeholder:text-gray-500 dark:placeholder:text-gray-500',
                      errors.password || validationErrors?.password
                        ? 'border-destructive'
                        : ''
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={
                      showPassword ? 'Hide password' : 'Show password'
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {errors.password && (
                  <p className="text-xs text-destructive">
                    {errors.password.message}
                  </p>
                )}
                {validationErrors?.password && (
                  <p className="text-xs text-destructive">
                    {validationErrors.password[0]}
                  </p>
                )}
                <PasswordStrengthIndicator password={password || ''} />
              </div>

              <div className="grid gap-3">
                <Label htmlFor="passwordConfirm">Confirm Password</Label>
                <Input
                  id="passwordConfirm"
                  type="password"
                  placeholder="Confirm your password"
                  {...register('passwordConfirm')}
                  className={cn(
                    'bg-white dark:bg-white border-gray-200 dark:border-gray-200 text-black dark:text-black placeholder:text-gray-500 dark:placeholder:text-gray-500',
                    errors.passwordConfirm || validationErrors?.passwordConfirm
                      ? 'border-destructive'
                      : ''
                  )}
                />
                {errors.passwordConfirm && (
                  <p className="text-xs text-destructive">
                    {errors.passwordConfirm.message}
                  </p>
                )}
                {validationErrors?.passwordConfirm && (
                  <p className="text-xs text-destructive">
                    {validationErrors.passwordConfirm[0]}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-black hover:bg-black/90 text-white"
                disabled={isSubmitting || isLoading}
              >
                {isSubmitting || isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create account'
                )}
              </Button>

              <div className="after:border-gray-200 dark:after:border-gray-200 relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
                <span className="bg-white dark:bg-white text-gray-500 dark:text-gray-500 relative z-10 px-2">
                  Or continue with
                </span>
              </div>

              <Button
                variant="outline"
                type="button"
                className="w-full bg-white dark:bg-white border-gray-200 dark:border-gray-200 text-black dark:text-black hover:bg-gray-50 dark:hover:bg-gray-50"
                onClick={handleGoogleSignUp}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                <span className="sr-only">Sign up with Google</span>
                Sign up with Google
              </Button>

              <div className="text-center text-sm">
                Already have an account?{' '}
                <Link to="/auth/login" className="underline underline-offset-4">
                  Sign in
                </Link>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
      <div className="text-center text-xs text-balance text-gray-400">
        By creating an account, you agree to our{' '}
        <a
          href="https://thoughtfabric.ai/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 hover:text-white transition-colors"
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a
          href="https://thoughtfabric.ai/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 hover:text-white transition-colors"
        >
          Privacy Policy
        </a>
        .
      </div>
    </div>
  )
}

function RegisterPage() {
  return (
    <BackgroundWrapper>
      <div className="flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <RegisterForm />
        </div>
      </div>
    </BackgroundWrapper>
  )
}

export const Route = createFileRoute('/auth/register/')({
  beforeLoad: async () => {
    const { useAuthStore } = await import('@/stores/auth-store')
    const { isAuthenticated } = useAuthStore.getState()

    // If user is already authenticated, redirect to dashboard
    if (isAuthenticated) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: RegisterPage,
})
